import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "sonner"
import { useUpdateDayTimesheet } from "@/hooks/useTimesheetRecords"
import { format, isValid } from "date-fns"

interface EditDayTimesheetModalProps {
  isOpen: boolean
  onClose: () => void
  employeeId: string
  employeeName: string
  date: string
  existingRecords?: any[]
  onSuccess?: () => void
}

export function EditDayTimesheetModal({ 
  isOpen, 
  onClose, 
  employeeId, 
  employeeName, 
  date, 
  existingRecords = [],
  onSuccess
}: EditDayTimesheetModalProps) {
  const [entrada, setEntrada] = useState('')
  const [saidaAlmoco, setSaidaAlmoco] = useState('')
  const [voltaAlmoco, setVoltaAlmoco] = useState('')
  const [saida, setSaida] = useState('')
  const [location, setLocation] = useState('')
  const [notes, setNotes] = useState('')

  const { mutate: updateDayTimesheet, isPending } = useUpdateDayTimesheet()

  useEffect(() => {
    if (isOpen && existingRecords.length > 0) {
      // Populate fields with existing records
      const entradaRecord = existingRecords.find(r => r.record_type === 'entrada')
      const saidaAlmocoRecord = existingRecords.find(r => r.record_type === 'saida_almoco')
      const voltaAlmocoRecord = existingRecords.find(r => r.record_type === 'volta_almoco')
      const saidaRecord = existingRecords.find(r => r.record_type === 'saida')

      // Parse the time correctly - handle both time string and full datetime
      const parseTime = (timeStr: string) => {
        if (!timeStr) return ''
        
        try {
          if (timeStr.includes('T')) {
            // Full datetime
            const date = new Date(timeStr)
            if (!isValid(date)) {
              console.warn('Invalid datetime string:', timeStr)
              return ''
            }
            return format(date, 'HH:mm')
          } else {
            // Just time string - validate format
            const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]/
            if (timeRegex.test(timeStr)) {
              return timeStr.substring(0, 5) // Get HH:mm part
            } else {
              console.warn('Invalid time format:', timeStr)
              return ''
            }
          }
        } catch (error) {
          console.warn('Error parsing time:', timeStr, error)
          return ''
        }
      }

      setEntrada(parseTime(entradaRecord?.record_time || ''))
      setSaidaAlmoco(parseTime(saidaAlmocoRecord?.record_time || ''))
      setVoltaAlmoco(parseTime(voltaAlmocoRecord?.record_time || ''))
      setSaida(parseTime(saidaRecord?.record_time || ''))
      setLocation(entradaRecord?.location || '')
      setNotes(entradaRecord?.notes || '')
    } else if (isOpen) {
      // Reset fields for new day
      setEntrada('')
      setSaidaAlmoco('')
      setVoltaAlmoco('')
      setSaida('')
      setLocation('')
      setNotes('')
    }
  }, [isOpen, existingRecords])

  const validateTimes = () => {
    const times = [entrada, saidaAlmoco, voltaAlmoco, saida].filter(Boolean)
    
    if (times.length === 0) {
      toast.error("Pelo menos um horário deve ser preenchido")
      return false
    }

    const toMinutes = (time: string) => {
      const [hours, minutes] = time.split(':').map(Number)
      return hours * 60 + minutes
    }

    const entradaMin = entrada ? toMinutes(entrada) : null
    const saidaAlmocoMin = saidaAlmoco ? toMinutes(saidaAlmoco) : null
    const voltaAlmocoMin = voltaAlmoco ? toMinutes(voltaAlmoco) : null
    const saidaMin = saida ? toMinutes(saida) : null

    // Almoço sempre deve ser no mesmo dia (entrada < saída almoço < volta almoço)
    if (entradaMin !== null && saidaAlmocoMin !== null && entradaMin >= saidaAlmocoMin) {
      toast.error("Saída para almoço deve ser após entrada")
      return false
    }
    
    if (saidaAlmocoMin !== null && voltaAlmocoMin !== null && saidaAlmocoMin >= voltaAlmocoMin) {
      toast.error("Volta do almoço deve ser após saída para almoço")
      return false
    }
    
    if (voltaAlmocoMin !== null && saidaMin !== null) {
      // Permitir virada de dia: se saída < volta almoço, adicionar 24h
      const saidaAjustada = saidaMin < voltaAlmocoMin ? saidaMin + 1440 : saidaMin
      if (voltaAlmocoMin >= saidaAjustada) {
        toast.error("Saída deve ser após volta do almoço")
        return false
      }
    }
    
    // Permitir entrada 17h e saída 06h (turno noturno)
    if (entradaMin !== null && saidaMin !== null && !saidaAlmocoMin && !voltaAlmocoMin) {
      const saidaAjustada = saidaMin < entradaMin ? saidaMin + 1440 : saidaMin
      if (entradaMin >= saidaAjustada) {
        toast.error("Saída deve ser após entrada")
        return false
      }
    }

    return true
  }

  const handleSave = () => {
    if (!validateTimes()) return

    const toISO = (timeStr: string, recordType: string) => {
      if (!timeStr) return null
      
      const [hours, minutes] = timeStr.split(':').map(Number)
      let dateToUse = date
      
      // Detectar virada de dia: se saída < entrada, adicionar +1 dia
      if (recordType === 'saida' && entrada) {
        const [entradaH] = entrada.split(':').map(Number)
        if (hours < entradaH) {
          // Adicionar 1 dia
          const d = new Date(date + 'T00:00:00')
          d.setDate(d.getDate() + 1)
          dateToUse = d.toISOString().split('T')[0]
        }
      }
      
      return new Date(`${dateToUse}T${timeStr}:00`).toISOString()
    }

    const records = []

    if (entrada) {
      records.push({
        record_type: 'entrada',
        record_time: toISO(entrada, 'entrada'),
        location,
        notes: notes ? `${notes} (Editado manualmente)` : 'Editado manualmente'
      })
    }

    if (saidaAlmoco) {
      records.push({
        record_type: 'saida_almoco',
        record_time: toISO(saidaAlmoco, 'saida_almoco'),
        location,
        notes: notes ? `${notes} (Editado manualmente)` : 'Editado manualmente'
      })
    }

    if (voltaAlmoco) {
      records.push({
        record_type: 'volta_almoco',
        record_time: toISO(voltaAlmoco, 'volta_almoco'),
        location,
        notes: notes ? `${notes} (Editado manualmente)` : 'Editado manualmente'
      })
    }

    if (saida) {
      records.push({
        record_type: 'saida',
        record_time: toISO(saida, 'saida'),
        location,
        notes: notes ? `${notes} (Editado manualmente)` : 'Editado manualmente'
      })
    }

    updateDayTimesheet({
      employeeId,
      date,
      records,
      existingRecords
    }, {
      onSuccess: () => {
        toast.success("Registros do dia atualizados com sucesso!")
        onSuccess?.()
        onClose()
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Editar Registros - {employeeName}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {(() => {
              try {
                const dateObj = new Date(date)
                if (!isValid(dateObj)) {
                  return 'Data inválida'
                }
                return format(dateObj, 'dd/MM/yyyy')
              } catch (error) {
                console.warn('Error formatting date:', date, error)
                return 'Data inválida'
              }
            })()}
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="entrada">Entrada</Label>
              <Input
                id="entrada"
                type="time"
                value={entrada}
                onChange={(e) => setEntrada(e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="saida-almoco">Saída Almoço</Label>
              <Input
                id="saida-almoco"
                type="time"
                value={saidaAlmoco}
                onChange={(e) => setSaidaAlmoco(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="volta-almoco">Volta Almoço</Label>
              <Input
                id="volta-almoco"
                type="time"
                value={voltaAlmoco}
                onChange={(e) => setVoltaAlmoco(e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="saida">Saída</Label>
              <Input
                id="saida"
                type="time"
                value={saida}
                onChange={(e) => setSaida(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="location">Local</Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Local de trabalho"
            />
          </div>

          <div>
            <Label htmlFor="notes">Observações</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observações adicionais"
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}